/* PQ Drawer Extras — REX C&C stock-refresh bridge + Yotpo points/redemption strip
   Loads with the cart drawer. Degrades gracefully: any failure hides the widget,
   never breaks the cart. */
(() => {
  'use strict';

  /* ============================================================
     0. Selly cart-wipe shield.
     Selly's engine (ac2_lt.js, TriggerCart) syncs its internal
     cart snapshot to the theme by force-clicking the drawer's
     qty/remove buttons. When its snapshot is stale-empty it sets
     quantities to 0 — wiping the shopper's real items (the
     “product + free gift both disappear” bug). Real shoppers
     produce trusted clicks; Selly's are synthetic. Block only
     untrusted clicks on the drawer's qty buttons.
     ============================================================ */
  document.addEventListener('click', (e) => {
    try {
      if (!e.isTrusted && e.target && e.target.closest && e.target.closest('cart-drawer-qty-button')) {
        e.stopImmediatePropagation();
        e.preventDefault();
        console.warn('[PQ] blocked scripted click on cart qty button (Selly cart-sync)');
      }
    } catch (err) { /* noop */ }
  }, true);


  /* ============================================================
     1. REX Click & Collect stock-refresh bridge
     The Maropost connector's own listener uses the deprecated
     DOMSubtreeModified event on #CartProducts (removed in Chrome
     ~mid-2024), so stock results never refresh on cart changes.
     This bridge re-triggers the connector's refresh after every
     cart mutation. The Maropost file itself is untouched.
     ============================================================ */
  let cncTimer;
  function refreshCnc() {
    clearTimeout(cncTimer);
    cncTimer = setTimeout(() => {
      try {
        const cnc = window.cnc;
        if (cnc && cnc.results && typeof cnc.results.isLoaded === 'function' && cnc.results.isLoaded()) {
          cnc.results.update();
        }
        // also notify any PQ fulfilment UI that the cart changed
        document.dispatchEvent(new CustomEvent('pq:cart-updated'));
      } catch (e) {
        console.warn('[PQ] C&C refresh skipped:', e);
      }
    }, 600);
  }
  /* Selly (free-gift app) shows its gift-picker in #tdf_quickview.
     If our drawer auto-opens at the same moment it buries the modal's
     buttons — looks like the cart "disappeared". While the modal is
     visible we veto the auto-open, then open the drawer once the
     shopper has dealt with the gift. */
  function sellyModalVisible() {
    const m = document.querySelector('#tdf_quickview .tdf_dialog_modal, .tdf_dialog_modal');
    return !!(m && m.offsetWidth > 0 && getComputedStyle(m).display !== 'none');
  }
  let sellyWaiter = null;
  function openDrawerAfterSelly() {
    if (sellyWaiter) return;
    sellyWaiter = setInterval(() => {
      if (sellyModalVisible()) return;
      clearInterval(sellyWaiter); sellyWaiter = null;
      synthesiseCartUpdate(); // fresh section (gift may have changed) + opens drawer
    }, 400);
    setTimeout(() => { if (sellyWaiter) { clearInterval(sellyWaiter); sellyWaiter = null; } }, 120000);
  }
  document.addEventListener('cart:update', (e) => {
    try {
      const d = e.detail && e.detail.data;
      if (d && d.drawer && d.drawer.open && sellyModalVisible()) {
        d.drawer.open = false;
        openDrawerAfterSelly();
      }
    } catch (err) { /* noop */ }
  }, true);

  /* Selly's modal often appears AFTER the drawer has already opened
     (its gift rules load async). Poll cheaply: if the modal is up and
     the drawer dialog is open, close the drawer and queue a reopen. */
  function drawerDialog() {
    const d = document.querySelector('cart-drawer');
    return d ? d.closest('dialog') : null;
  }
  setInterval(() => {
    try {
      if (!sellyModalVisible()) return;
      const dlg = drawerDialog();
      if (dlg && dlg.open) { dlg.close(); }
      openDrawerAfterSelly();
    } catch (e) { /* noop */ }
  }, 300);

  /* ============================================================
     0b. Header cart icon -> open drawer (not the /cart page).
     The theme's cart icon is a plain link to /cart, so shoppers
     land on the legacy cart page (no coupon entry / Yotpo). For
     trusted clicks we open the drawer instead. /cart remains the
     no-JS fallback and is untouched when already on that page or
     when the drawer isn't present.
     ============================================================ */
  document.addEventListener('click', (e) => {
    try {
      if (!e.isTrusted || !e.target || !e.target.closest) return;
      const a = e.target.closest('a[href="/cart"], a[href^="/cart?"], a[href$="/cart"]');
      if (!a) return;
      if (a.closest('cart-drawer')) return;               // explicit "view cart" inside drawer
      if (location.pathname === '/cart') return;          // already on cart page
      if (!document.querySelector('cart-drawer')) return; // no drawer on this template
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return; // new-tab intents
      e.preventDefault();
      e.stopPropagation();
      synthesiseCartUpdate(); // fresh section + opens drawer
    } catch (err) { /* noop */ }
  }, true);

  /* ============================================================
     0c. Boost (AI Search & Discovery) add-to-cart -> drawer.
     Boost renders collection/search grids with its own quick-add
     button. Its Ajax cart is disabled (enableCart:false), so after
     a successful AJAX add it hard-redirects to /cart — bypassing
     the drawer. There is no supported hook to open a theme drawer,
     so we intercept the click in the capture phase (before Boost's
     React handler), perform the add ourselves, and let the foreign
     add bridge below refresh + open the drawer.
     Fallback ladder: multi-variant or any failure -> navigate to
     the product page (never a dead click).
     ============================================================ */
  document.addEventListener('click', (e) => {
    try {
      if (!e.target || !e.target.closest) return;
      const btn = e.target.closest('.boost-sd__btn-add-to-cart');
      if (!btn) return;
      if (!document.querySelector('cart-drawer')) return; // no drawer: leave Boost alone
      if (e.metaKey || e.ctrlKey || e.shiftKey || (e.button && e.button !== 0)) return;
      const card = btn.closest('.boost-sd__product-item');
      const link = card && card.querySelector('a[href*="/products/"]');
      const m = link && String(link.getAttribute('href')).match(/\/products\/([^/?#]+)/);
      if (!m) return; // can't resolve product: leave Boost alone
      e.preventDefault();
      e.stopPropagation(); // capture phase: Boost's handler never runs
      const handle = m[1];
      const pdpUrl = '/products/' + handle;
      const prevDisabled = btn.disabled;
      if ('disabled' in btn) btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      fetch('/products/' + handle + '.js')
        .then((r) => { if (!r.ok) throw new Error('product fetch ' + r.status); return r.json(); })
        .then((product) => {
          const variants = product.variants || [];
          const avail = variants.filter((v) => v.available);
          let chosen = null;
          if (variants.length === 1) {
            chosen = avail[0] || null;
          } else if (variants.length > 1) {
            /* Boost's "Select options" popup lets the shopper pick a
               variant via swatches — honour that selection instead of
               bouncing to the PDP. Read each swatch group's selected
               option and match it against the product's variants. */
            const norm = (s) => String(s == null ? '' : s).trim().toLowerCase();
            const sel = {};
            const groups = card ? card.querySelectorAll('.boost-sd__product-swatch') : [];
            groups.forEach((g) => {
              const title = g.querySelector('.boost-sd__product-swatch-title');
              const opt = g.querySelector('.boost-sd__product-swatch-option--selected');
              if (title && opt) {
                const name = norm(String(title.textContent).split(':')[0]);
                if (name) sel[name] = norm(opt.textContent);
              }
            });
            const names = (product.options || []).map((o) => norm(o && o.name != null ? o.name : o));
            if (Object.keys(sel).length && names.length && names.every((n) => sel[n] != null)) {
              chosen = variants.find((v) => v.available &&
                names.every((n, i) => norm(v.options && v.options[i]) === sel[n])) || null;
            }
          }
          if (!chosen) {
            // no/ambiguous selection (or sold out) -> PDP handles it properly
            window.location.assign(pdpUrl);
            return null;
          }
          const fd = new FormData();
          fd.append('id', String(chosen.id));
          fd.append('quantity', '1');
          return fetch('/cart/add.js', { method: 'POST', body: fd })
            .then((r) => { if (!r.ok) throw new Error('add failed ' + r.status); return r.json(); });
          // drawer opens via the foreign add bridge (fetch hook -> onForeignCartAdd)
        })
        .catch((err) => {
          console.warn('[PQ] Boost quick-add bridge failed, falling back to PDP:', err);
          window.location.assign(pdpUrl);
        })
        .finally(() => {
          if ('disabled' in btn) btn.disabled = prevDisabled;
          btn.removeAttribute('aria-busy');
        });
    } catch (err) { /* noop: Boost's native behaviour remains */ }
  }, true);

  // theme event bus (covers drawer qty buttons + add-to-cart)
  let lastCartUpdateAt = 0;
  document.addEventListener('cart:update', () => { lastCartUpdateAt = Date.now(); refreshCnc(); });

  /* Foreign add-to-cart bridge: page builders (e.g. PageFly PDPs) add to
     cart with their own AJAX and never publish through the theme's event
     bus — so the drawer doesn't open and the header badge goes stale.
     If no cart:update follows a /cart/add within 800ms, we synthesise one
     with a fresh drawer section + cart meta (same payload shape as
     cart-provider.js), which opens the drawer and refreshes counters. */
  async function synthesiseCartUpdate() {
    try {
      const [metaRes, sectionRes] = await Promise.all([
        fetch('/cart?view=meta'),
        fetch('/?section_id=g_cart-drawer'),
      ]);
      const meta = await metaRes.json();
      const section = await sectionRes.text();
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: false, cancelable: true, composed: true,
        detail: { sectionId: null, data: {
          meta: { count: Number(meta.count), total: meta.total },
          drawer: { section, open: true },
        } },
      }));
    } catch (e) { console.warn('[PQ] synthetic cart update failed:', e); }
  }

  function onForeignCartAdd() {
    const stamp = Date.now();
    setTimeout(() => {
      if (lastCartUpdateAt < stamp) synthesiseCartUpdate();
    }, 800);
  }

  /* R26: settle refresh. Apps that mutate the cart in several steps (Selly's
     gift flow: add gift -> /cart/update.js with a signed attribute that its
     Shopify discount reads) leave the drawer rendered from an intermediate
     state: gift line at full price, checkout total without the discount, while
     checkout itself is right. After the LAST cart write settles we re-fetch
     the drawer section and, only if the rendered total differs, re-render in
     place without changing whether the drawer is open. */
  let settleTimer;
  function drawerIsOpen() {
    const cd = document.querySelector('cart-drawer');
    return !!(cd && cd.offsetWidth > 0 && cd.offsetHeight > 0);
  }
  function scheduleSettleRefresh() {
    clearTimeout(settleTimer);
    settleTimer = setTimeout(async () => {
      try {
        const [metaRes, sectionRes] = await Promise.all([
          origFetch('/cart?view=meta'),
          origFetch('/?section_id=g_cart-drawer'),
        ]);
        const meta = await metaRes.json();
        const section = await sectionRes.text();
        const doc = new DOMParser().parseFromString(section, 'text/html');
        const stash = doc.querySelector('#cart-drawer-stash');
        const root = stash ? stash.content : doc;
        // Compare what the shopper sees (lines + quantities + prices + totals) with the fresh render.
        // Selly decorates live amounts with a struck original ("$195.98 $90.99"), so compare on the
        // per-line keys/quantities and the LAST amount in each part rather than raw text.
        const live = document.querySelector('cart-drawer') || document;
        const sig = (scope) => {
          const lines = Array.from(scope.querySelectorAll('[data-item-key]')).map((li) => {
            const q = li.querySelector('input[name="updates[]"], [class*="product-qty"]:not([class*="button"])');
            const price = li.querySelector('[class*="product-card-min-price"]');
            return li.getAttribute('data-item-key') + ':' + (q ? (q.value || q.textContent).trim() : '') + ':' + (price ? (price.textContent.match(/\$[\d,]+\.\d{2}|FREE/g) || []).pop() : '');
          });
          const t = scope.querySelector('[data-dynamic="total-inline"]');
          const total = t ? (t.textContent.match(/\$[\d,]+\.\d{2}/g) || []).pop() : '';
          return lines.join('|') + '#' + total;
        };
        if (sig(root) === sig(live)) return;
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: false, cancelable: true, composed: true,
          detail: { sectionId: null, data: {
            meta: { count: Number(meta.count), total: meta.total },
            drawer: { section, open: drawerIsOpen() && !sellyModalVisible() },
          } },
        }));
      } catch (e) { console.warn('[PQ] settle refresh skipped:', e); }
    }, 1200);
  }

  // safety net: any script mutating the cart via fetch or XHR
  const origFetch = window.fetch;
  window.fetch = function (...args) {
    const p = origFetch.apply(this, args);
    try {
      const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
      if (/\/cart\/(add|change|update|clear)/.test(url)) {
        p.then(() => { refreshCnc(); scheduleSettleRefresh(); if (/\/cart\/add/.test(url)) onForeignCartAdd(); }, () => {});
      }
    } catch (e) { /* noop */ }
    return p;
  };
  /* Scripted native submits: Selly's gift flow re-submits the product form
     via form.submit(), which fires NO submit event and full-page POSTs to
     /cart/add -> Shopify redirects to the native /cart page. Convert any
     scripted submit of a cart-add form into an AJAX add + drawer refresh. */
  const origFormSubmit = HTMLFormElement.prototype.submit;
  HTMLFormElement.prototype.submit = function () {
    try {
      const action = String(this.getAttribute('action') || this.action || '');
      if (/\/cart\/add/.test(action) && document.querySelector('cart-drawer')) {
        const fd = new FormData(this);
        fetch('/cart/add.js', { method: 'POST', body: fd })
          .then((r) => { if (!r.ok) throw new Error('add failed ' + r.status); return r.json(); })
          .then(() => { refreshCnc(); onForeignCartAdd(); })
          .catch(() => { origFormSubmit.call(this); }); // fail open: native behaviour
        return;
      }
    } catch (e) { /* fall through to native */ }
    return origFormSubmit.call(this);
  };

  /* R22: sendBeacon net — fire-and-forget cart writes (some apps use beacons
     for prop rewrites) otherwise produce no load event and no cart:update.
     Fail-open: always calls through; worst case = no signal, same as before. */
  if (navigator.sendBeacon) {
    const origBeacon = navigator.sendBeacon.bind(navigator);
    navigator.sendBeacon = function (url, data) {
      try {
        if (typeof url === 'string' && /\/cart\/(add|change|update|clear)/.test(url)) refreshCnc();
      } catch (e) { /* noop */ }
      return origBeacon(url, data);
    };
  }

  const origXhrOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    try {
      if (typeof url === 'string' && /\/cart\/(add|change|update|clear)/.test(url)) {
        this.addEventListener('load', () => {
          refreshCnc();
          scheduleSettleRefresh();
          if (/\/cart\/add/.test(url)) onForeignCartAdd();
        });
      }
    } catch (e) { /* noop */ }
    return origXhrOpen.call(this, method, url, ...rest);
  };

  /* ============================================================
     2. Discount code entry — collapsed by default (CRO), applies
     via the /discount/CODE cookie route, then refreshes the
     drawer's applied-discounts display to confirm.
     ============================================================ */
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  async function refreshDiscountsPart() {
    // Re-fetch the drawer section and swap in the fresh applied-discounts part
    try {
      const html = await (await fetch('/?section_id=g_cart-drawer')).text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const stash = doc.querySelector('#cart-drawer-stash');
      const root = stash ? stash.content : doc;
      const fresh = root.querySelector('[data-dynamic="pq-discounts"]');
      const live = document.querySelector('cart-drawer [data-dynamic="pq-discounts"]') ||
                   document.querySelector('[data-dynamic="pq-discounts"]');
      if (fresh && live) {
        live.replaceChildren(...fresh.childNodes);
        return live.querySelector('.pq-coupon__chip') != null;
      }
    } catch (e) { console.warn('[PQ] discount refresh failed:', e); }
    return false;
  }

  /* Validate via the Cart API: POST the code list to /cart/update.js,
     then read back cart.discount_codes[].applicable. Returns:
     'ok' (valid & applied), 'invalid' (rejected — removed again),
     or 'unknown' (API doesn't expose validation — cookie fallback). */
  async function applyCode(code) {
    try {
      const cart = await fetch('/cart.js').then((r) => r.json());
      const existing = (cart.discount_codes || []).map((d) => d.code);
      if (existing.some((c) => c.toLowerCase() === code.toLowerCase())) {
        await refreshDiscountsPart();
        return 'ok';
      }
      const codes = existing.concat(code).join(',');
      const updated = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: codes }),
      }).then((r) => r.json());
      const entry = (updated.discount_codes || []).find(
        (d) => d.code.toLowerCase() === code.toLowerCase()
      );
      if (entry && entry.applicable === false) {
        // put the cart back how it was
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: existing.join(',') }),
        }).catch(() => {});
        return 'invalid';
      }
      await refreshDiscountsPart();
      if (entry && entry.applicable === true) return 'ok';
    } catch (e) { console.warn('[PQ] cart-api discount failed:', e); }
    // fallback: legacy cookie route (validated at checkout)
    await fetch('/discount/' + encodeURIComponent(code)).catch(() => {});
    const confirmed = await refreshDiscountsPart();
    return confirmed ? 'ok' : 'unknown';
  }

  async function removeCode(code) {
    try {
      const cart = await fetch('/cart.js').then((r) => r.json());
      const remaining = (cart.discount_codes || [])
        .map((d) => d.code)
        .filter((c) => c.toLowerCase() !== String(code).toLowerCase());
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: remaining.join(',') }),
      });
      await synthesiseCartUpdate(); // re-render totals without the discount
      const msg = document.querySelector('[data-pq-coupon-msg]');
      if (msg) { msg.textContent = ''; msg.className = 'pq-coupon__msg'; }
      return true;
    } catch (e) { console.warn('[PQ] discount remove failed:', e); return false; }
  }

  // Delegated so it survives drawer re-renders
  document.addEventListener('click', async (e) => {
    const btn = e.target && e.target.closest && e.target.closest('[data-pq-coupon-remove]');
    if (!btn) return;
    e.preventDefault();
    if (btn.disabled) return;
    btn.disabled = true;
    await removeCode(btn.getAttribute('data-pq-coupon-remove'));
  });

  function wireCoupon() {
    const toggle = document.querySelector('[data-pq-coupon-toggle]');
    const form = document.querySelector('[data-pq-coupon-form]');
    const input = document.querySelector('[data-pq-coupon-input]');
    const apply = document.querySelector('[data-pq-coupon-apply]');
    const msg = document.querySelector('[data-pq-coupon-msg]');
    if (!toggle || !form || !input || !apply) return;

    toggle.addEventListener('click', () => {
      const open = form.hidden;
      form.hidden = !open;
      toggle.setAttribute('aria-expanded', String(open));
      if (open) input.focus();
    });

    const submit = async () => {
      const code = (input.value || '').trim();
      if (!code) return;
      apply.disabled = true;
      apply.textContent = 'Applying\u2026';
      if (msg) { msg.textContent = ''; msg.className = 'pq-coupon__msg'; }
      const result = await applyCode(code);
      if (result === 'ok') {
        // Re-render the whole drawer so subtotal + checkout button reflect
        // the discount immediately (chips alone aren't enough).
        await synthesiseCartUpdate();
      }
      apply.disabled = false;
      apply.textContent = 'Apply';
      if (msg) {
        if (result === 'ok') {
          msg.textContent = 'Discount applied \u2713';
          msg.className = 'pq-coupon__msg is-ok';
          input.value = '';
          form.hidden = true;
          toggle.setAttribute('aria-expanded', 'false');
        } else if (result === 'invalid') {
          msg.textContent = "That code didn't apply here. Gift cards and rewards vouchers are applied at checkout \u2014 otherwise, check the spelling and try again.";
          msg.className = 'pq-coupon__msg is-err';
        } else {
          msg.textContent = 'Code added \u2014 if valid, your discount will show at checkout.';
          msg.className = 'pq-coupon__msg is-note';
        }
      }
    };
    apply.addEventListener('click', submit);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); submit(); }
    });
  }

  /* ============================================================
     3. Public surface for sibling modules (pq-rewards.js applies
     voucher codes through the exact same validated path as the
     manual coupon field, so behaviour is identical).
     ============================================================ */
  window.PQDrawer = Object.assign(window.PQDrawer || {}, {
    applyCode: applyCode,
    refreshDrawer: synthesiseCartUpdate,
  });

  /* ============================================================
     4. Fulfilment selector — init once (widget persists across
     drawer re-renders; only [data-dynamic] parts are replaced),
     refresh stock whenever the cart changes.
     ============================================================ */
  function bootFulfilment() {
    const root = document.getElementById('pq-fulfilment');
    if (!root || !window.PQFulfilment || window.pqf) return;
    try {
      window.pqf = new window.PQFulfilment({
        subdomain: ((window.Shopify && window.Shopify.shop) || location.hostname).split('.', 1),
        apiPrefix: 'shopify.retailexpress.com.au'
      });
    } catch (e) { console.warn('[PQ] fulfilment init failed:', e); }
  }

  /* NOTE: no pq:cart-updated listener here — PQFulfilment.watchCartChanges
     already listens on the event bus (since Round 8). A second listener here
     double-fetched stock and double-rendered the store list on every cart
     mutation, which showed up as the store selector flickering open/closed
     on mobile (Round 10 fix). */

  function boot() {
    wireCoupon();
    // PQFulfilment loads deferred; poll briefly until it's defined
    let tries = 0;
    const t = setInterval(() => {
      bootFulfilment();
      if (window.pqf || ++tries > 20) clearInterval(t);
    }, 250);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
